<template>
  <div
    role="main"
    aria-label="Liste des paiements livreur"
    tabindex="-1"
  >
    <!-- Enhanced Filter Section -->
    <b-card
      class="mb-3 payments-filters-card"
      body-class="pt-2"
    >
      <template #header>
        <div class="d-flex justify-content-between align-items-center">
          <h5 class="mb-0">
            <feather-icon
              icon="FilterIcon"
              class="mr-2"
            />
            Filtres de recherche des paiements
          </h5>
          <b-badge
            v-if="hasActiveFilters"
            variant="primary"
            pill
          >
            {{ appliedFilters.length }} filtre{{ appliedFilters.length > 1 ? 's' : '' }} actif{{ appliedFilters.length > 1 ? 's' : '' }}
          </b-badge>
        </div>
      </template>

      <!-- Applied Filters Display -->
      <div
        v-if="hasActiveFilters"
        class="mb-3 applied-filters-section"
        aria-label="Filtres appliqués"
        aria-live="polite"
      >
        <div class="d-flex align-items-center mb-2">
          <h6 class="mb-0 mr-2">
            Filtres appliqués:
          </h6>
          <b-button
            variant="outline-danger"
            size="sm"
            @click="clearAllFilters"
          >
            <feather-icon
              icon="XIcon"
              size="14"
              class="mr-1"
            />
            Effacer tous
          </b-button>
        </div>
        <div class="d-flex flex-wrap">
          <b-badge
            v-for="filter in appliedFilters"
            :key="filter.key"
            :variant="filter.variant || 'primary'"
            class="mr-2 mb-2 p-2 applied-filter-badge"
            @click="removeFilter(filter.key)"
          >
            <feather-icon
              v-if="filter.icon"
              :icon="filter.icon"
              size="12"
              class="mr-1"
            />
            {{ filter.label }}: {{ filter.value }}
            <feather-icon
              icon="XIcon"
              size="12"
              class="ml-1 cursor-pointer"
            />
          </b-badge>
        </div>
      </div>

      <!-- Filters Row -->
      <b-row
        class="mt-1"
        aria-label="Filtres principaux de recherche"
      >
        <b-col md="3">
          <b-form-group
            label="Numéro de paiement"
            label-for="num"
          >
            <b-form-input
              v-model="filters.num"
              v-debounce:1000="applyFilters"
              placeholder="Rechercher par numéro"
              type="text"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="Livreur"
            label-for="deliveryMan"
          >
            <v-select
              v-model="filters.deliveryMan"
              :clearable="true"
              placeholder="Sélectionner un livreur"
              label="username"
              :options="deliveryMans"
              @input="applyFilters"
            />
          </b-form-group>
        </b-col>
        <b-col
          v-if="user.role === 'admin'"
          md="3"
        >
          <b-form-group
            label="Dépôt"
            label-for="repository"
          >
            <v-select
              v-model="filters.repository"
              :clearable="true"
              placeholder="Sélectionner un dépôt"
              label="name"
              :options="repositories"
              @input="applyFilters"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="Date de début"
            label-for="dateFrom"
          >
            <b-form-datepicker
              v-model="filters.dateFrom"
              v-bind="datePickerConfig"
              placeholder="Date de début"
              reset-button
              close-button
              @input="applyFilters"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Second Filter Row -->
      <b-row
        class="align-items-end"
        aria-label="Filtres secondaires de recherche"
      >
        <b-col md="3">
          <b-form-group
            label="Date de fin"
            label-for="dateTo"
          >
            <b-form-datepicker
              v-model="filters.dateTo"
              v-bind="datePickerConfig"
              placeholder="Date de fin"
              reset-button
              close-button
              @input="applyFilters"
            />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Statut de validation">
            <div class="validation-filter-container">
              <b-form-radio-group
                v-model="filters.validationStatus"
                :options="validationStatusOptions"
                buttons
                button-variant="outline-primary"
                size="sm"
                @change="applyFilters"
              />
              <small class="text-muted d-block mt-1">
                <template v-if="filters.validationStatus === 'all'">
                  Affichage de tous les paiements
                </template>
                <template v-else-if="filters.validationStatus === 'validated'">
                  Uniquement les paiements validés
                </template>
                <template v-else-if="filters.validationStatus === 'pending'">
                  Uniquement les paiements en attente
                </template>
              </small>
            </div>
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Montant minimum (DT)">
            <b-form-input
              v-model="filters.minAmount"
              v-debounce:1000="applyFilters"
              placeholder="0.000"
              type="number"
              step="0.001"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Montant maximum (DT)">
            <b-form-input
              v-model="filters.maxAmount"
              v-debounce:1000="applyFilters"
              placeholder="999.999"
              type="number"
              step="0.001"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row class="mt-2 justify-content-end">
        <b-col
          md="auto"
          class="d-flex justify-content-end"
        >
          <b-button
            variant="dark"
            class="mr-1 btn-black"
            @click="openPaymentsStatistics"
          >
            <feather-icon icon="TrendingUpIcon" />
            <span class="ml-1">Statistiques du jour</span>
          </b-button>
          <b-button
            v-if="user && (user.role === 'admin' || user.role === 'manager')"
            :to="{ name: 'payment-add' }"
            variant="gradient-primary"
          >
            <feather-icon icon="PlusIcon" />
            <span class="ml-1">Ajouter</span>
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Enhanced KPIs Row with Loading State -->
    <b-row
      v-if="!isLoading && payments.length > 0"
      class="mb-3"
      aria-label="Indicateurs clés de performance (KPI) des paiements livreur"
      role="region"
    >
      <b-col md="2">
        <b-card class="text-center bg-light-primary kpi-card">
          <div class="card-body p-2">
            <div class="d-flex align-items-center justify-content-center mb-1">
              <feather-icon
                icon="FileTextIcon"
                size="16"
                class="text-primary mr-1"
              />
              <h4 class="text-primary mb-0">
                {{ count }}
              </h4>
            </div>
            <small class="text-primary font-weight-semibold">Total Paiements</small>
          </div>
        </b-card>
      </b-col>
      <b-col md="2">
        <b-card class="text-center bg-light-success kpi-card">
          <div class="card-body p-2">
            <div class="d-flex align-items-center justify-content-center mb-1">
              <feather-icon
                icon="CheckCircleIcon"
                size="16"
                class="text-success mr-1"
              />
              <h4 class="text-success mb-0">
                {{ validatedPaymentsCount }}
              </h4>
            </div>
            <small class="text-success font-weight-semibold">Validés</small>
          </div>
        </b-card>
      </b-col>
      <b-col md="2">
        <b-card class="text-center bg-light-warning kpi-card">
          <div class="card-body p-2">
            <div class="d-flex align-items-center justify-content-center mb-1">
              <feather-icon
                icon="ClockIcon"
                size="16"
                class="text-warning mr-1"
              />
              <h4 class="text-warning mb-0">
                {{ pendingPaymentsCount }}
              </h4>
            </div>
            <small class="text-warning font-weight-semibold">En Attente</small>
          </div>
        </b-card>
      </b-col>
      <b-col md="2">
        <b-card class="text-center bg-light-info kpi-card">
          <div class="card-body p-2">
            <div class="d-flex align-items-center justify-content-center mb-1">
              <feather-icon
                icon="PackageIcon"
                size="16"
                class="text-info mr-1"
              />
              <h4 class="text-info mb-0">
                {{ totalOrdersCount }}
              </h4>
            </div>
            <small class="text-info font-weight-semibold">Total Colis</small>
          </div>
        </b-card>
      </b-col>
      <b-col md="2">
        <b-card class="text-center bg-light-secondary kpi-card">
          <div class="card-body p-2">
            <div class="d-flex align-items-center justify-content-center mb-1">
              <feather-icon
                icon="RefreshCwIcon"
                size="16"
                class="text-secondary mr-1"
              />
              <h4 class="text-secondary mb-0">
                {{ totalExchangeCount }}
              </h4>
            </div>
            <small class="text-secondary font-weight-semibold">Échanges</small>
          </div>
        </b-card>
      </b-col>
      <b-col md="2">
        <b-card class="text-center bg-light-danger kpi-card">
          <div class="card-body p-2">
            <div class="d-flex align-items-center justify-content-center mb-1">
              <feather-icon
                icon="TrendingUpIcon"
                size="16"
                class="text-danger mr-1"
              />
              <h4 class="text-danger mb-0">
                {{ averageDeliveryPrice.toFixed(3) }}
              </h4>
            </div>
            <small class="text-danger font-weight-semibold">Moy. Livraison DT</small>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Loading State for KPIs -->
    <b-row
      v-if="isLoading"
      class="mb-3"
      aria-busy="true"
      aria-live="polite"
      aria-label="Chargement des indicateurs KPI"
    >
      <b-col
        v-for="i in 6"
        :key="i"
        md="2"
      >
        <b-card class="text-center">
          <div class="card-body p-2">
            <b-skeleton
              width="60%"
              height="24px"
              class="mx-auto mb-1"
            />
            <b-skeleton
              width="80%"
              height="14px"
              class="mx-auto"
            />
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Total Delivery Price Display -->
    <b-alert
      v-if="user.role === 'admin' && totalDeliveryPrice > 0 && payments.length > 0"
      variant="info"
      show
      class="mb-3 total-delivery-alert"
      aria-label="Total prix de livraison pour tous les paiements affichés"
      role="status"
    >
      <div class="d-flex align-items-center">
        <feather-icon
          icon="DollarSignIcon"
          class="mr-2"
        />
        <strong>Total Prix de Livraison: {{ totalDeliveryPrice.toFixed(3) }} DT</strong>
      </div>
    </b-alert>

    <!-- Loading Spinner -->
    <div
      v-if="isLoading"
      class="text-center mb-2"
    >
      <b-spinner
        variant="primary"
        label="Chargement..."
      />
    </div>

    <!-- No Results Found -->
    <div
      v-if="!isLoading && payments.length === 0"
      class="text-center py-5"
      aria-live="polite"
      aria-label="Aucun paiement livreur trouvé"
      role="status"
    >
      <b-card
        class="shadow-sm no-results-card"
        aria-label="Carte d'absence de résultats"
      >
        <div class="mb-3">
          <feather-icon
            icon="SearchIcon"
            size="48"
            class="text-muted"
          />
        </div>
        <h5 class="text-muted mb-3">
          Aucun paiement trouvé
        </h5>
        <p class="text-muted mb-4">
          <span v-if="hasActiveFilters">
            Aucun paiement ne correspond aux critères de recherche actuels.
            <br>
            Essayez de modifier ou supprimer certains filtres.
          </span>
          <span v-else>
            Il n'y a actuellement aucun paiement dans le système.
          </span>
        </p>
        <div v-if="hasActiveFilters">
          <b-button
            variant="outline-primary"
            @click="clearAllFilters"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
              class="mr-1"
            />
            Réinitialiser les filtres
          </b-button>
        </div>
        <div v-else-if="user.role === 'admin' || user.role === 'manager'">
          <b-button
            :to="{ name: 'payment-add' }"
            variant="primary"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
              class="mr-1"
            />
            Créer votre premier paiement
          </b-button>
        </div>
      </b-card>
    </div>

    <!-- Results Table -->
    <vue-good-table
      v-if="!isLoading && payments.length > 0"
      :columns="tableColumns"
      :rows="payments"
      :search-options="{ enabled: false }"
      :pagination-options="{ enabled: true, perPage: pageLength }"
      aria-label="Tableau des paiements livreur"
      role="table"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <div
          v-if="props.column.field === 'id'"
          class="text-nowrap"
        >
          <qrcode-vue
            style="display:inline"
            :value="props.row.id.toString()"
            :size="size"
            level="H"
          />
          <p class="ml-2">
            {{ props.row.id }}
          </p>
        </div>

        <!-- Column: Delivery Man -->
        <div
          v-else-if="props.column.field === 'delivery_man'"
          class="text-nowrap"
        >
          <b-avatar
            :src="props.row.delivery_man && props.row.delivery_man.avatar"
            class="mx-1"
          />
          <span class="text-nowrap">{{ props.row.delivery_man && props.row.delivery_man.username || '-' }}</span>
        </div>

        <!-- Column: Orders Summary -->
        <div
          v-else-if="props.column.field === 'orders_summary'"
          class="text-center"
        >
          <div class="d-flex justify-content-center align-items-center">
            <b-badge
              variant="success"
              class="mr-1"
              pill
            >
              <feather-icon
                icon="PackageIcon"
                size="12"
                class="mr-1"
              />
              {{ props.row.delivred_orders_count }}
            </b-badge>
            <b-badge
              variant="warning"
              class="mr-1"
              pill
            >
              <feather-icon
                icon="RotateCcwIcon"
                size="12"
                class="mr-1"
              />
              {{ props.row.return_orders_count }}
            </b-badge>
            <b-badge
              v-if="props.row.exchange_orders_count > 0"
              variant="info"
              pill
            >
              <feather-icon
                icon="RefreshCwIcon"
                size="12"
                class="mr-1"
              />
              {{ props.row.exchange_orders_count }}
            </b-badge>
          </div>
        </div>

        <!-- Column: Validation Status -->
        <div
          v-else-if="props.column.field === 'is_validated'"
          class="text-center"
        >
          <b-badge
            :variant="props.row.is_validated ? 'success' : 'warning'"
            pill
          >
            <feather-icon
              :icon="props.row.is_validated ? 'CheckCircleIcon' : 'ClockIcon'"
              size="12"
              class="mr-1"
            />
            {{ props.row.is_validated ? 'Validé' : 'En attente' }}
          </b-badge>
        </div>

        <!-- Column: Action -->
        <div
          v-else-if="props.column.field === 'action'"
          class="d-flex align-items-center"
        >
          <router-link
            class="text-info"
            :to="{ name: 'payment-show', params: { id: props.row.id} }"
          >
            <feather-icon
              :id="`payment-row-${props.row.id}-print-icon`"
              icon="PrinterIcon"
              class="cursor-pointer mr-1"
              size="16"
            />
          </router-link>
          <b-tooltip
            title="Imprimer"
            class="cursor-pointer"
            :target="`payment-row-${props.row.id}-print-icon`"
          />

          <router-link
            class="text-primary"
            :to="{ name: 'payment-kpi', params: { id: props.row.id} }"
          >
            <feather-icon
              v-b-tooltip.hover
              icon="BarChart3Icon"
              class="cursor-pointer mr-1"
              size="16"
              title="Statistiques & KPI"
            />
          </router-link>

          <feather-icon
            v-if="user.role === 'admin' && !props.row.is_validated"
            :id="`validate-payment-${props.row.id}`"
            icon="CheckIcon"
            class="cursor-pointer mr-1 text-success"
            size="16"
            @click="validatePayment(props.row.id)"
          />
          <b-tooltip
            v-if="user.role === 'admin' && !props.row.is_validated"
            title="Valider"
            class="cursor-pointer"
            :target="`validate-payment-${props.row.id}`"
          />
        </div>

        <!-- Column: Common -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- Enhanced Pagination -->
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap align-items-center">
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap mr-2">
              Affichage {{ ((currentPage - 1) * pageLength) + 1 }} à {{ Math.min(currentPage * pageLength, count) }}
            </span>
            <b-form-select
              v-model="pageLength"
              :options="[
                { value: 50, text: '50' },
                { value: 100, text: '100' },
                { value: 500, text: '500' }
              ]"
              class="mx-1"
              size="sm"
              style="width: 80px;"
              @input="(value) => {
                props.perPageChanged({currentPerPage:value});
                applyFilters();
              }"
            />
            <span class="text-nowrap ml-2"> de {{ count }} entrées</span>
          </div>

          <div>
            <b-pagination
              v-model="currentPage"
              :total-rows="count"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              size="sm"
              @input="pageNext(currentPage)"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>

    <!-- Enhanced Summary Card for Admin Users -->
    <b-card
      v-if="!isLoading && payments.length > 0 && user.role === 'admin'"
      class="mt-3"
      header="Résumé Détaillé des Paiements"
      aria-label="Résumé détaillé des paiements livreur"
      role="region"
    >
      <b-row>
        <b-col md="2">
          <div class="text-center">
            <h4 class="text-primary">
              {{ count }}
            </h4>
            <p class="text-muted">
              Total Paiements
            </p>
          </div>
        </b-col>
        <b-col md="2">
          <div class="text-center">
            <h4 class="text-success">
              {{ validatedPaymentsCount }}
            </h4>
            <p class="text-muted">
              Paiements Validés
            </p>
          </div>
        </b-col>
        <b-col md="2">
          <div class="text-center">
            <h4 class="text-warning">
              {{ pendingPaymentsCount }}
            </h4>
            <p class="text-muted">
              En Attente
            </p>
          </div>
        </b-col>
        <b-col md="2">
          <div class="text-center">
            <h4 class="text-info">
              {{ totalDeliveryPrice.toFixed(3) }} DT
            </h4>
            <p class="text-muted">
              Total Livraisons
            </p>
          </div>
        </b-col>
        <b-col md="2">
          <div class="text-center">
            <h4 class="text-secondary">
              {{ totalExchangeCount }}
            </h4>
            <p class="text-muted">
              Total Échanges
            </p>
          </div>
        </b-col>
        <b-col md="2">
          <div class="text-center">
            <h4 class="text-danger">
              {{ (totalDeliveredPrice + totalReturnPrice).toFixed(3) }} DT
            </h4>
            <p class="text-muted">
              Chiffre d'Affaires
            </p>
          </div>
        </b-col>
      </b-row>
    </b-card>

    <!-- Today's Payments Statistics Sidebar -->
    <b-sidebar
      id="payments-statistics-sidebar"
      v-model="showPaymentsStatistics"
      title="Statistiques Paiements"
      right
      shadow
      width="700px"
      backdrop
    >
      <template #header="{ hide }">
        <div class="d-flex align-items-center justify-content-between w-100 p-3">
          <h4 class="mb-0">
            <feather-icon
              icon="TrendingUpIcon"
              size="24"
              class="mr-2 text-primary"
            />
            Tableau de Bord - Paiements du jour
          </h4>
          <b-button
            variant="link"
            class="text-dark"
            @click="hide"
          >
            <feather-icon icon="XIcon" />
          </b-button>
        </div>
      </template>

      <div class="px-3 pb-3">
        <div
          v-if="paymentsStatisticsLoading"
          class="text-center py-5"
        >
          <b-spinner
            variant="primary"
            label="Chargement..."
          />
          <p class="mt-3 text-muted">
            Chargement des statistiques de paiements...
          </p>
        </div>

        <div v-else-if="paymentsStatistics">
          <div class="mb-3">
            <b-badge
              variant="light-primary"
              class="p-2"
            >
              <feather-icon
                icon="CalendarIcon"
                size="14"
              />
              {{ formatStatisticsDate(paymentsStatistics.date) }}
            </b-badge>
          </div>

          <h5 class="mb-3">
            <feather-icon
              icon="PieChartIcon"
              size="18"
              class="mr-1"
            />
            Vue d'ensemble
          </h5>
          <b-row class="mb-4">
            <b-col
              cols="6"
              class="mb-3"
            >
              <b-card class="stat-card text-center border-primary">
                <feather-icon
                  icon="FileTextIcon"
                  size="32"
                  class="text-primary mb-2"
                />
                <h3 class="font-weight-bold text-primary mb-0">
                  {{ paymentsStatistics.overview.total_payments }}
                </h3>
                <p class="text-muted mb-0 small">
                  Paiements créés aujourd'hui
                </p>
              </b-card>
            </b-col>
            <b-col
              cols="6"
              class="mb-3"
            >
              <b-card class="stat-card text-center border-info">
                <feather-icon
                  icon="ArrowDownCircleIcon"
                  size="32"
                  class="text-info mb-2"
                />
                <h3 class="font-weight-bold text-info mb-0">
                  {{ paymentsStatistics.overview.total_delivered_amount }} DT
                </h3>
                <p class="text-muted mb-0 small">
                  Montant livré
                </p>
              </b-card>
            </b-col>
            <b-col
              cols="6"
              class="mb-3"
            >
              <b-card class="stat-card text-center border-warning">
                <feather-icon
                  icon="RotateCcwIcon"
                  size="32"
                  class="text-warning mb-2"
                />
                <h3 class="font-weight-bold text-warning mb-0">
                  {{ paymentsStatistics.overview.total_return_amount }} DT
                </h3>
                <p class="text-muted mb-0 small">
                  Montant retourné
                </p>
              </b-card>
            </b-col>
          </b-row>

          <b-card class="bg-gradient-primary text-white mb-4">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h5 class="text-white mb-1">
                  Frais de livraison total
                </h5>
                <h2 class="font-weight-bold text-white mb-0">
                  {{ paymentsStatistics.overview.total_delivery_fees }} DT
                </h2>
              </div>
              <feather-icon
                icon="TruckIcon"
                size="48"
                class="text-white opacity-50"
              />
            </div>
          </b-card>

          <b-row class="mb-4">
            <b-col
              cols="6"
              class="mt-3"
            >
              <b-card class="text-center">
                <p class="text-muted mb-1 small">
                  Paiements en attente
                </p>
                <h4 class="font-weight-bold mb-0 text-warning">
                  {{ paymentsStatistics.overview.pending_payments_count }}
                </h4>
              </b-card>
            </b-col>
          </b-row>

          <h5 class="mb-3">
            <feather-icon
              icon="ActivityIcon"
              size="18"
              class="mr-1"
            />
            Validation
          </h5>
          <b-card class="mb-4">
            <div class="mb-3">
              <div class="d-flex justify-content-between mb-2">
                <span>Validés</span>
                <span class="font-weight-bold">
                  {{ paymentsStatistics.status_breakdown.validated }} / {{ paymentsStatistics.overview.total_payments }}
                </span>
              </div>
              <b-progress
                :value="paymentsStatistics.status_breakdown.validation_rate"
                :max="100"
                variant="success"
                height="8px"
              />
              <small class="text-muted">
                {{ paymentsStatistics.status_breakdown.validation_rate }}%
              </small>
            </div>
            <div>
              <div class="d-flex justify-content-between mb-2">
                <span>En attente</span>
                <span class="font-weight-bold">
                  {{ paymentsStatistics.status_breakdown.pending }} / {{ paymentsStatistics.overview.total_payments }}
                </span>
              </div>
              <b-progress
                :value="paymentsStatistics.status_breakdown.pending"
                :max="paymentsStatistics.overview.total_payments || 1"
                variant="warning"
                height="8px"
              />
            </div>
          </b-card>

          <h5 class="mb-3">
            <feather-icon
              icon="UsersIcon"
              size="18"
              class="mr-1"
            />
            Performance des livreurs
          </h5>
          <div
            v-if="paymentsStatistics.drivers.length > 0"
            class="mb-4"
          >
            <b-card
              v-for="driver in paymentsStatistics.drivers"
              :key="driver.id"
              class="mb-3"
            >
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <h6 class="mb-1 font-weight-bold">
                    <feather-icon
                      icon="UserIcon"
                      size="16"
                      class="mr-1"
                    />
                    {{ driver.username }}
                  </h6>
                  <small
                    v-if="driver.repository"
                    class="text-muted d-block"
                  >
                    <feather-icon
                      icon="HomeIcon"
                      size="12"
                    />
                    {{ driver.repository }}
                  </small>
                  <small
                    v-if="driver.phone"
                    class="text-muted"
                  >
                    <feather-icon
                      icon="PhoneIcon"
                      size="12"
                      class="mr-1"
                    />
                    {{ driver.phone }}
                  </small>
                </div>
                <b-badge
                  variant="light-primary"
                  class="p-2"
                >
                  {{ driver.payments_count }} paiement(s)
                </b-badge>
              </div>

              <b-row class="mt-2">
                <b-col cols="6">
                  <small class="text-muted d-block">Montant livré</small>
                  <strong>{{ driver.delivered_amount_total }} DT</strong>
                </b-col>
                <b-col cols="6">
                  <small class="text-muted d-block">Retours</small>
                  <strong>{{ driver.return_amount_total }} DT</strong>
                </b-col>
              </b-row>

              <div class="mt-2">
                <small class="text-muted">Paiements :</small>
                <router-link
                  v-for="(paymentId, index) in driver.payment_ids"
                  :key="paymentId"
                  :to="{ name: 'payment-show', params: { id: paymentId } }"
                  class="text-primary"
                >
                  #{{ paymentId }}<span v-if="index < driver.payment_ids.length - 1">, </span>
                </router-link>
              </div>
            </b-card>
          </div>
          <b-alert
            v-else
            variant="info"
            show
          >
            <feather-icon
              icon="InfoIcon"
              class="mr-1"
            />
            Aucun paiement enregistré aujourd'hui.
          </b-alert>

          <h5
            v-if="paymentsStatistics.repositories.length > 0"
            class="mb-3"
          >
            <feather-icon
              icon="HomeIcon"
              size="18"
              class="mr-1"
            />
            Performance par dépôt
          </h5>
          <div
            v-if="paymentsStatistics.repositories.length > 0"
            class="mb-4"
          >
            <b-card
              v-for="repo in paymentsStatistics.repositories"
              :key="repo.id || repo.name"
              class="mb-2"
            >
              <b-row class="align-items-center">
                <b-col cols="6">
                  <h6 class="mb-1 font-weight-bold">
                    {{ repo.name }}
                  </h6>
                  <small class="text-muted">
                    {{ repo.payments_count }} paiement(s) · {{ repo.validated_count }} validé(s)
                  </small>
                </b-col>
                <b-col cols="6">
                  <small class="text-muted d-block">Livré: {{ repo.delivered_amount_total }} DT</small>
                  <small class="text-muted d-block">Retours: {{ repo.return_amount_total }} DT</small>
                </b-col>
              </b-row>
            </b-card>
          </div>

          <h5 class="mb-3">
            <feather-icon
              icon="ClockIcon"
              size="18"
              class="mr-1"
            />
            Derniers paiements créés
          </h5>
          <b-card>
            <div v-if="paymentsStatistics.recent_payments.length > 0">
              <div
                v-for="payment in paymentsStatistics.recent_payments"
                :key="payment.id"
                class="d-flex justify-content-between align-items-center py-2 border-bottom"
              >
                <div>
                  <strong>#{{ payment.id }}</strong>
                  <div class="text-muted small">
                    {{ payment.delivery_man || 'Livreur inconnu' }} · {{ payment.repository || 'Sans dépôt' }}
                  </div>
                </div>
                <div class="text-right">
                  <div class="font-weight-bold text-primary">
                    {{ payment.collectible_amount }} DT
                  </div>
                  <small :class="payment.is_validated ? 'text-success' : 'text-warning'">
                    {{ payment.is_validated ? 'Validé' : 'En attente' }}
                  </small>
                </div>
              </div>
            </div>
            <div
              v-else
              class="text-center text-muted py-3"
            >
              Aucun paiement récent aujourd'hui.
            </div>
          </b-card>
        </div>

        <div
          v-else
          class="text-center py-5"
        >
          <feather-icon
            icon="AlertCircleIcon"
            size="48"
            class="text-danger mb-3"
          />
          <p class="text-muted">
            Erreur lors du chargement des statistiques des paiements
          </p>
          <b-button
            variant="outline-primary"
            size="sm"
            @click="loadPaymentsStatistics"
          >
            <feather-icon icon="RefreshCwIcon" />
            Réessayer
          </b-button>
        </div>
      </div>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BAvatar, BPagination, BFormGroup, BFormInput, BFormSelect, BButton, BRow, BCol,
  BSpinner, BTooltip, BBadge, BCard, BAlert, BFormDatepicker, BFormRadioGroup,
  BSkeleton, BSidebar, BProgress,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import axios from 'axios'
import QrcodeVue from 'qrcode.vue'
import vSelect from 'vue-select'
import storeAuth from '@/store/store'

export default {
  components: {
    VueGoodTable,
    BAvatar,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BButton,
    BSpinner,
    BTooltip,
    BRow,
    BCol,
    QrcodeVue,
    vSelect,
    BBadge,
    BCard,
    BAlert,
    BFormDatepicker,
    BFormRadioGroup,
    BSkeleton,
    BSidebar,
    BProgress,
  },
  data() {
    return {
      exportedPayments: null,
      totalDeliveryPrice: 0,
      isFiltered: false,
      size: 80,
      isLoading: false,
      pageLength: 100,
      count: 0,
      dir: false,
      currentPage: 1,
      rangePicker: ['', ''],
      columns: [
        { label: 'Reference', field: 'id' },
        { label: 'Livreur', field: 'delivery_man.username' },
        { label: 'Depot', field: 'repository' },
        { label: 'Colis (L/R/E)', field: 'orders_summary' },
        { label: 'Total livraison', field: 'total_delivery_price', formatFn: value => `${value} DT` },
        { label: 'Total Prix TTC', field: 'delivred_price', formatFn: value => `${value} DT` },
        { label: 'Statut', field: 'is_validated' },
        {
          label: 'Date | Heure',
          field: 'created_at',
          formatFn: value => {
            if (!value) return ''
            const dateObj = new Date(value)
            const date = dateObj.toLocaleDateString('fr-FR')
            const time = dateObj.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
            return `${date} ${time}`
          },
        },
        { label: 'Action', field: 'action' },
      ],
      exportSalesToExcelFields: {
        id: 'id',
        depot: 'depot',
        livreur: 'livreur',
        nombre_colis_livre: 'nombre_colis_livre',
        nombre_colis_retour: 'nombre_colis_retour',
        total_prix_ttc: 'total_prix_ttc',
        total_frais_livraison: 'total_frais_livraison',
        created_at: 'created_at',
      },
      // Initialize with proper default values
      filters: {
        num: '',
        deliveryMan: null,
        repository: null,
        dateFrom: null,
        dateTo: null,
        validationStatus: 'all', // 'all', 'validated', 'pending'
        minAmount: '',
        maxAmount: '',
      },

      // Validation status options for radio buttons
      validationStatusOptions: [
        { text: 'Tous', value: 'all' },
        { text: 'Validés', value: 'validated' },
        { text: 'En attente', value: 'pending' },
      ],

      // Applied filters for display
      appliedFilters: [],

      // Date picker configuration
      datePickerConfig: {
        locale: 'fr-FR',
        startWeekday: 1, // Monday
        labelHelp: 'Utilisez les flèches pour naviguer',
        labelPrevYear: 'Année précédente',
        labelPrevMonth: 'Mois précédent',
        labelCurrentMonth: 'Mois actuel',
        labelNextMonth: 'Mois suivant',
        labelNextYear: 'Année suivante',
        labelToday: 'Aujourd\'hui',
        labelSelected: 'Date sélectionnée',
        labelNoDateSelected: 'Aucune date sélectionnée',
        labelCalendar: 'Calendrier',
        labelNav: 'Navigation du calendrier',
      },

      // Data arrays
      payments: [],
      deliveryMans: [],
      repositories: [],

      // Payments statistics sidebar state
      showPaymentsStatistics: false,
      paymentsStatistics: null,
      paymentsStatisticsLoading: false,
    }
  },
  computed: {
    /**
     * Check if there are active filters
     */
    hasActiveFilters() {
      return this.appliedFilters.length > 0
    },

    /**
     * Calculate validated payments count
     */
    validatedPaymentsCount() {
      return this.payments.filter(payment => payment.is_validated).length
    },

    /**
     * Calculate pending payments count
     */
    pendingPaymentsCount() {
      return this.payments.filter(payment => !payment.is_validated).length
    },

    /**
     * Calculate total orders count (delivered + returned + exchanged)
     */
    totalOrdersCount() {
      return this.payments.reduce((total, payment) => total + payment.delivred_orders_count + payment.return_orders_count + (payment.exchange_orders_count || 0), 0)
    },

    /**
     * Calculate total exchange orders count
     */
    totalExchangeCount() {
      return this.payments.reduce((total, payment) => total + (payment.exchange_orders_count || 0), 0)
    },

    /**
     * Calculate total delivered price
     */
    totalDeliveredPrice() {
      return this.payments.reduce((total, payment) => total + (parseFloat(payment.delivred_price) || 0), 0)
    },

    /**
     * Calculate total return price
     */
    totalReturnPrice() {
      return this.payments.reduce((total, payment) => total + (parseFloat(payment.return_price) || 0), 0)
    },

    /**
     * Calculate average delivery price
     */
    averageDeliveryPrice() {
      if (this.payments.length === 0) return 0
      return this.totalDeliveryPrice / this.payments.length
    },

    tableColumns() {
      let filteredColumns = this.columns
      if (this.user.role === 'manager') {
        filteredColumns = this.columns.filter(column => column.field !== 'total_delivery_price' && column.field !== 'repository')
      } else if (this.user.role !== 'admin') {
        filteredColumns = this.columns.filter(column => column.field !== 'total_delivery_price')
      }
      return filteredColumns
    },
  },
  watch: {
    num() {
      if (!this.awaitingSearch) {
        setTimeout(() => {
          this.getPaymentFromId()
          this.awaitingSearch = false
        }, 1000)
      }
      this.awaitingSearch = true
    },
  },
  created() {
    this.user = storeAuth.state.user
    this.initializeData()
  },
  methods: {
    /**
     * Initialize all required data on component creation
     */
    async initializeData() {
      try {
        // Load all required data in parallel
        await Promise.all([
          this.getDeliveryMans(),
          this.getRepositories(),
          this.fetchPayments(),
        ])
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error initializing data:', error)
        this.showToast('danger', 'top-center', 'Erreur lors de l\'initialisation des données')
      }
    },

    /**
     * Open payments statistics sidebar
     */
    async openPaymentsStatistics() {
      this.showPaymentsStatistics = true
      await this.loadPaymentsStatistics()
    },

    /**
     * Load payments statistics from API
     */
    async loadPaymentsStatistics() {
      this.paymentsStatisticsLoading = true
      try {
        const { data } = await axios.get('/api/payments/today-statistics/')
        this.paymentsStatistics = data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading payments statistics:', error)
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des statistiques de paiements')
        this.paymentsStatistics = null
      } finally {
        this.paymentsStatisticsLoading = false
      }
    },

    /**
     * Format statistics date to French locale
     */
    formatStatisticsDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    },

    /**
     * Get repositories - fix the missing data issue
     */
    async getRepositories() {
      try {
        const response = await axios.get('/api/repositories/')
        this.repositories = response.data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching repositories:', error)
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des dépôts')
      }
    },

    /**
     * Get delivery men
     */
    async getDeliveryMans() {
      try {
        this.isLoading = true
        const response = await axios.get('/api/auth/users/drivers/')
        this.deliveryMans = response.data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching delivery men:', error)
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des livreurs')
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Apply all filters and fetch payments
     */
    async applyFilters() {
      this.currentPage = 1
      this.updateAppliedFilters()
      await this.fetchPayments()
    },

    /**
     * Format date for API (ensure YYYY-MM-DD format)
     */
    formatDateForAPI(date) {
      if (!date) return ''
      // Bootstrap Vue datepicker returns date in YYYY-MM-DD format
      return date
    },

    /**
     * Fetch payments with current filters
     */
    async fetchPayments() {
      this.isLoading = true
      this.exportedPayments = null

      // Build query parameters from filters
      const params = {
        page: this.currentPage,
        page_size: this.pageLength,
      }

      try {
        // Add non-empty filters to params
        if (this.filters.deliveryMan && this.filters.deliveryMan.id) {
          params.delivery_man = this.filters.deliveryMan.id
        }

        if (this.filters.repository && this.filters.repository.id) {
          params.repository = this.filters.repository.id
        }

        // Handle validation status filter
        if (this.filters.validationStatus === 'validated') {
          params.is_validated = true
        } else if (this.filters.validationStatus === 'pending') {
          params.is_validated = false
        }
        // If 'all' is selected, don't add is_validated parameter

        // Handle amount filtering
        if (this.filters.minAmount && this.filters.minAmount > 0) {
          params.min_amount = this.filters.minAmount
        }

        if (this.filters.maxAmount && this.filters.maxAmount > 0) {
          params.max_amount = this.filters.maxAmount
        }

        // Handle date range with new separate date fields
        if (this.filters.dateFrom) {
          params.start_date = this.formatDateForAPI(this.filters.dateFrom)
        }

        if (this.filters.dateTo) {
          params.end_date = this.formatDateForAPI(this.filters.dateTo)
        }

        // Handle number search using django filter (?id=)
        if (this.filters.num && this.filters.num.length >= 1) {
          params.id = this.filters.num
        }
        // Use the regular payments endpoint with filters
        const { data } = await axios.get('/api/payments/', {
          params,
          timeout: 60000, // 60 second timeout for large result sets
        })
        this.payments = data.results
        this.count = data.count
        this.totalDeliveryPrice = data.total_delivery_price || 0
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching payments:', error)
        // eslint-disable-next-line no-console
        console.error('Request params:', params)

        // Provide more specific error messages
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          this.showToast('warning', 'top-center', 'La requête a pris trop de temps. Essayez de réduire la taille de la page.')
        } else if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
          this.showToast('danger', 'top-center', 'Erreur réseau. Vérifiez votre connexion ou essayez avec moins de résultats par page.')
        } else {
          this.showToast('danger', 'top-center', 'Erreur lors du chargement des paiements')
        }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Update the applied filters display
     */
    updateAppliedFilters() {
      this.appliedFilters = []

      // Text filters
      if (this.filters.num) {
        this.appliedFilters.push({
          key: 'num',
          label: 'Numéro',
          value: this.filters.num,
          icon: 'SearchIcon',
          variant: 'info',
        })
      }

      // Select filters
      if (this.filters.deliveryMan) {
        this.appliedFilters.push({
          key: 'deliveryMan',
          label: 'Livreur',
          value: this.filters.deliveryMan.username,
          icon: 'UserIcon',
          variant: 'secondary',
        })
      }

      if (this.filters.repository) {
        this.appliedFilters.push({
          key: 'repository',
          label: 'Dépôt',
          value: this.filters.repository.name,
          icon: 'MapPinIcon',
          variant: 'secondary',
        })
      }

      // Validation status filter
      if (this.filters.validationStatus !== 'all') {
        const statusMap = {
          validated: {
            label: 'Statut', value: 'Validés uniquement', icon: 'CheckCircleIcon', variant: 'success',
          },
          pending: {
            label: 'Statut', value: 'En attente uniquement', icon: 'ClockIcon', variant: 'warning',
          },
        }

        if (statusMap[this.filters.validationStatus]) {
          this.appliedFilters.push({
            key: 'validationStatus',
            ...statusMap[this.filters.validationStatus],
          })
        }
      }

      // Amount filters
      if (this.filters.minAmount) {
        this.appliedFilters.push({
          key: 'minAmount',
          label: 'Montant min.',
          value: `${this.filters.minAmount} DT`,
          icon: 'TrendingUpIcon',
          variant: 'primary',
        })
      }

      if (this.filters.maxAmount) {
        this.appliedFilters.push({
          key: 'maxAmount',
          label: 'Montant max.',
          value: `${this.filters.maxAmount} DT`,
          icon: 'TrendingDownIcon',
          variant: 'primary',
        })
      }

      // Date filters with new separate fields
      if (this.filters.dateFrom) {
        this.appliedFilters.push({
          key: 'dateFrom',
          label: 'Date début',
          value: this.formatDate(this.filters.dateFrom),
          icon: 'CalendarIcon',
          variant: 'info',
        })
      }

      if (this.filters.dateTo) {
        this.appliedFilters.push({
          key: 'dateTo',
          label: 'Date fin',
          value: this.formatDate(this.filters.dateTo),
          icon: 'CalendarIcon',
          variant: 'info',
        })
      }
    },

    /**
     * Format date for display
     */
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString('fr-FR')
    },

    /**
     * Remove a specific filter
     */
    removeFilter(filterKey) {
      if (filterKey === 'deliveryMan' || filterKey === 'repository') {
        this.filters[filterKey] = null
      } else if (filterKey === 'validationStatus') {
        this.filters[filterKey] = 'all'
      } else {
        this.filters[filterKey] = ''
      }
      this.applyFilters()
    },

    /**
     * Clear all filters
     */
    clearAllFilters() {
      this.filters = {
        num: '',
        deliveryMan: null,
        repository: null,
        dateFrom: null,
        dateTo: null,
        validationStatus: 'all',
        minAmount: '',
        maxAmount: '',
      }
      this.totalDeliveryPrice = 0
      this.appliedFilters = []
      this.applyFilters()
    },

    /**
     * Handle page change
     */
    async pageNext(page) {
      this.currentPage = page
      await this.fetchPayments()
    },
    async validatePayment(payload) {
      // Show confirmation dialog
      const result = await this.$swal({
        title: 'Confirmer la validation',
        text: 'Êtes-vous sûr de vouloir valider ce paiement ?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Oui, valider',
        cancelButtonText: 'Annuler',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })

      // If user confirmed, proceed with validation
      if (result.isConfirmed) {
        try {
          this.isLoading = true
          const response = await axios.put(`api/payments/validate/${payload}/`)
          if (response.status === 200) {
            await this.fetchPayments() // Use fetchPayments instead of getPayments
            this.showToast('success', 'top-center', 'Paiement validé avec succès')
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error validating payment:', error)
          this.showToast('danger', 'top-center', 'Erreur lors de la validation du paiement')
        } finally {
          this.isLoading = false
        }
      }
    },
    /**
     * Show toast notification - simplified version
     */
    showToast(variant, position, text) {
      this.$toast({
        title: 'Notification',
        text,
        variant,
        solid: true,
      })
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import '@core/scss/vue/libs/vue-good-table.scss';

.applied-filters {
  .badge {
    font-size: 0.875rem;
    padding: 0.5rem 0.75rem;
  }
}

// Enhanced styling for no results card
.no-results-card {
  border: 2px dashed #e3e6f0;
  background-color: #f8f9fc;

  .feather-icon {
    opacity: 0.5;
  }
}

// Table styling improvements
.vue-good-table {
  .font-weight-semibold {
    font-weight: 600;
  }

  .text-right {
    text-align: right;
  }

  .text-center {
    text-align: center;
  }
}

// Pagination improvements
.pagination {
  .page-link {
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
  }
}

.payments-filters-card {
  .card-header {
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
  }
}

// Enhanced KPI cards styling
.bg-light-primary {
  background-color: rgb(115, 103, 240) !important;
}

.bg-light-success {
  background-color: rgb(40, 199, 111) !important;
}

.bg-light-warning {
  background-color: rgb(255, 159, 67) !important;
}

.bg-light-info {
  background-color: rgb(0, 207, 232) !important;
}

.bg-light-secondary {
  background-color: rgb(130, 134, 139) !important;
}

.bg-light-danger {
  background-color: rgb(234, 84, 85) !important;
}

// Enhanced badge styling
.badge {
  display: inline-flex;
  align-items: center;
}

// Enhanced applied filters section
.applied-filters-section {
  background-color: #f8f9fc;
  border-radius: 0.375rem;
  padding: 1rem;
  border: 1px solid #e3e6f0;
}

.applied-filter-badge {
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  }
}

// Total delivery bar spacing/shape
.total-delivery-alert {
  padding: 1rem 1.25rem !important;
  border-radius: 12px !important;
  margin-top: 1rem !important;
  margin-bottom: 1rem !important;
}

// Validation filter styling
.validation-filter-container {
  .btn-group .btn {
    font-size: 0.875rem;
    padding: 0.375rem 0.75rem;
  }

  .btn-outline-primary:not(:disabled):not(.disabled).active,
  .btn-outline-primary:not(:disabled):not(.disabled):active {
    background-color: rgba(234, 84, 85, 0.18) !important;
    border-color: rgba(234, 84, 85, 0.55) !important;
    color: #000000 !important;
  }

  .btn-outline-primary.active {
    background-color: var(--bs-primary);
    border-color: var(--bs-primary);
    color: white;
  }
}

// Enhanced KPI cards
.kpi-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid #e3e6f0;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  }

  .card-body {
    padding: 0.75rem !important;
  }
}

// Improved skeleton loading
.b-skeleton {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

// Statistics Sidebar Styling
.stat-card {
  transition: all 0.3s ease;
  border-width: 2px;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 4px 25px rgba(0, 0, 0, 0.1);
  }
}

.b-sidebar {
  .b-sidebar-body {
    overflow-y: auto;
    max-height: calc(100vh - 80px);
  }
}

.opacity-50 {
  opacity: 0.5;
}
</style>

<style lang="scss" scoped>
.total-price-shadow {
  text-shadow: 5px 5px 5px rgba(197, 65, 65, 0.3);
  padding: 10px;
  display: inline-block;
}

.kpi-card {
  border: none !important;

  &.bg-light-primary {
    background-color: rgba(115, 103, 240, 0.12) !important;
    .text-primary { color: #7367F0 !important; }
  }
  &.bg-light-success {
    background-color: rgba(40, 199, 111, 0.12) !important;
    .text-success { color: #28C76F !important; }
  }
  &.bg-light-warning {
    background-color: rgba(255, 159, 67, 0.12) !important;
    .text-warning { color: #FF9F43 !important; }
  }
  &.bg-light-info {
    background-color: rgba(0, 207, 232, 0.12) !important;
    .text-info { color: #00CFE8 !important; }
  }
  &.bg-light-secondary {
    background-color: rgba(130, 134, 139, 0.12) !important;
    .text-secondary { color: #82868B !important; }
  }
  &.bg-light-danger {
    background-color: rgba(234, 84, 85, 0.12) !important;
    .text-danger { color: #EA5455 !important; }
  }
}
</style>
